import { IPROP_MAP, modifyProps } from '../../generators/modifyProps';
import { useEffect, useState } from 'react';

import { JDSDropdownProps } from './generated';
import { getRequiredLabel } from '../jdsinputfield/logic';
import { setPositions } from '../jdsmenublock/positions';
import { useRootRef } from '../../hooks/useRootRef';
import { useSelected } from '../../hooks/useSelected';
import { useToggle } from '../../hooks/useToggle';

export interface DropdownProps
  extends Omit<
    JDSDropdownProps,
    'current' | 'onAppear' | 'onOpen' | 'onSelect' | 'onClose'
  > {
  stateConfig?: {
    errorText?: string;
    successText?: string;
    warningText?: string;
    requiredText?: string;
  };
  onOpen?: () => void;
  onClose?: () => void;
  onSelect?: (index: number, value: string) => void;
  value?: string;
  prefix?: string | JSX.Element;
  required?: boolean;
}

export const PROP_MAP: IPROP_MAP<DropdownProps> = (props) => {
  return {
    state: {
      properties: {
        state: props.state
      },
      variations: {
        error: {
          stateText: props.stateConfig?.errorText
        },
        success: {
          stateText: props.stateConfig?.successText
        },
        warning: {
          stateText: props.stateConfig?.warningText
        },
        required: {
          stateText: props.stateConfig?.requiredText
        }
      }
    },
    items: {
      properties: {
        items: props.items || []
      }
    }
  };
};

const SELECT_CONTAINER_SELECTOR = 'div#select-container';
const OFFSET = 12;

export const withLogic =
  (Component: React.ComponentType<DropdownProps>): React.FC<DropdownProps> =>
  (userProps: DropdownProps) => {
    const props = modifyProps(userProps, PROP_MAP) as DropdownProps;
    const [distance, setDistance] = useState({
      x: 0,
      y: 0,
      width: 0,
      height: 0
    });
    const { selectedIndex, items = [], onSelect } = props;
    const { index, onClick } = useSelected(selectedIndex);
    const { selected, toggle, close } = useToggle(props.open);
    const { element, rootRef } = useRootRef();
    const selectContainer = element?.querySelector(SELECT_CONTAINER_SELECTOR);

    useEffect(() => {
      if (selectContainer && selected) {
        const {
          height,
          left: x,
          top: y,
          width = 0
        } = setPositions(selectContainer, undefined);

        setDistance({ x, y, width, height });
      }
    }, [selected, selectContainer]);

    const hasPrefix = Boolean(props.prefix);

    const currentIdx = props.value
      ? items.findIndex((item) => item.value === props.value)
      : index;

    return (
      <Component
        {...{
          ...props,
          label: getRequiredLabel(props.label, props.required),
          current: items[currentIdx]?.label,
          open: selected,
          onSelect: (index: number) => {
            onClick(index);
            onSelect?.(
              index,
              items[index]?.value || (items[index]?.label as string)
            );
          },
          rootRef,
          _hasPrefix: hasPrefix,
          _inputFocus: currentIdx >= 0,
          selectedIndex: currentIdx,
          onClose: () => {
            close();
            props.onClose?.();
          },
          onOpen: () => {
            toggle();

            props.onOpen?.();
          },
          _labelFocus: Boolean(currentIdx >= 0 || props.placeholder),
          _offsetX: distance.x,
          _offsetY: distance.y + OFFSET,
          _maxWidth: distance.width,
          _maxHeight: distance.height,
          _isValueEmpty: !items[currentIdx]?.label,
          _isPlaceHolderEmpty: !props.placeholder
        }}
      />
    );
  };
